import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useProject, useProjects, useRun, useRuns } from "../api/hooks";
import type { ProjectSummary } from "../api/types";
import { EdgeResizer } from "../components/Split";
import { StatusDot, Toasts } from "../components/ui";
import { AssistantPanel } from "../features/assistant/AssistantPanel";
import { DeliveriesPanel } from "../features/platform/DeliveriesPanel";
import { TenantMenu } from "../features/platform/TenantMenu";
import { CreateProjectDialog } from "../features/projects/CreateProjectDialog";
import { allProblems, useProblems } from "./problems";
import { useSession, type DockTab } from "./store";

const NAV: { to: string; label: string; ico: string; count?: (p: ProjectSummary | undefined) => number | undefined }[] = [
  { to: "/flights", label: "Flights", ico: "✈" },
  { to: "/frame", label: "Frame", ico: "▦" },
  { to: "/derived", label: "Derived", ico: "ƒ", count: (p) => p?.derived?.length },
  { to: "/inputs", label: "Inputs", ico: "⚙", count: (p) => p?.inputs?.length },
  { to: "/triggers", label: "Triggers", ico: "⚡", count: (p) => p?.triggers?.length },
  { to: "/reports", label: "Reports", ico: "📄", count: (p) => p?.reports?.length },
  { to: "/analysis", label: "Analysis", ico: "📈" },
  { to: "/runs", label: "Runs", ico: "▶" },
  { to: "/tests", label: "Tests", ico: "✓" },
  { to: "/versions", label: "Versions", ico: "⌥" },
];

const DELIVERY_NAV: typeof NAV = [
  { to: "/live", label: "Live", ico: "◉" },
  { to: "/subscriptions", label: "Subscriptions", ico: "✉" },
];

export function Shell() {
  const {
    projectId,
    setProject,
    theme,
    toggleTheme,
    railCollapsed,
    toggleRail,
    dockOpen,
    dockTab,
    setDock,
    railWidth,
    setRailWidth,
    dockHeight,
    setDockHeight,
    assistantOpen,
    setAssistantOpen,
  } = useSession();
  const projects = useProjects();
  const project = useProject(projectId);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const liveMode = location.pathname.startsWith("/live");

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  // pick the first project automatically
  useEffect(() => {
    if (!projects.data) return;
    if (projectId && projects.data.some((p) => p.id === projectId)) return;
    setProject(projects.data[0]?.id ?? null);
  }, [projects.data, projectId, setProject]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "j") {
        e.preventDefault();
        setDock(!dockOpen);
      }
      if (e.ctrlKey && e.key.toLowerCase() === "b") {
        e.preventDefault();
        toggleRail();
      }
      if (e.ctrlKey && e.key.toLowerCase() === "i") {
        e.preventDefault();
        setAssistantOpen(!assistantOpen);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dockOpen, setDock, toggleRail, assistantOpen, setAssistantOpen]);

  return (
    <div className="shell">
      <header className="hdr">
        <div className="hdr-brand">
          <span className="logo" aria-hidden="true" />
          <span>
            Reports <b>Studio</b>
          </span>
        </div>
        <span className="hdr-sep" />
        <select className="inp" value={projectId ?? ""} onChange={(e) => setProject(e.target.value || null)} title="Project">
          {(projects.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.bundled ? " (example)" : ""}
            </option>
          ))}
        </select>
        <button className="btn sm" onClick={() => setCreating(true)} title="New project">
          + New
        </button>
        <span className="hdr-sep" />
        <div className="mode-pill" title="Run mode">
          <button className={liveMode ? "" : "on"} onClick={() => liveMode && navigate("/analysis")} title="Replay recorded flights">
            Replay
          </button>
          <button className={liveMode ? "on" : ""} onClick={() => !liveMode && navigate("/live")} title="Live streaming (NATS / demo)">
            Live
          </button>
        </div>
        <div className="hdr-right">
          <RunIndicator onClick={() => navigate("/runs")} />
          <button
            className={`btn ghost sm ai-toggle${assistantOpen ? " on" : ""}`}
            onClick={() => setAssistantOpen(!assistantOpen)}
            title="AI assistant: build triggers, derived parameters, inputs and reports (Ctrl+I)"
          >
            ✦ Assistant
          </button>
          <button className="btn ghost sm" onClick={toggleTheme} title="Toggle theme">
            {theme === "dark" ? "☾" : "☀"}
          </button>
          <TenantMenu />
        </div>
      </header>
      <div
        className={`shell-body${railCollapsed ? " rail-collapsed" : ""}${assistantOpen ? " assistant-open" : ""}`}
        style={{ "--rail-w": `${railWidth}px` } as CSSProperties}
      >
        <nav className="rail">
          {!railCollapsed ? <EdgeResizer axis="x" className="rail-resizer" value={railWidth} onChange={setRailWidth} onReset={() => setRailWidth(208)} title="Drag to resize sidebar · double-click to reset · Ctrl+B to collapse" /> : null}
          <div className="rail-group">Project</div>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="ico">{n.ico}</span>
              <span>{n.label}</span>
              {n.count?.(project.data) !== undefined ? <span className="count">{n.count(project.data)}</span> : null}
            </NavLink>
          ))}
          <div className="rail-group">Live &amp; delivery</div>
          {DELIVERY_NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "active" : "")}>
              <span className="ico">{n.ico}</span>
              <span>{n.label}</span>
            </NavLink>
          ))}
          <a
            href="#"
            onClick={(e) => (e.preventDefault(), setDock(true, "deliveries"))}
            title="Delivery history (dock)"
          >
            <span className="ico">⇪</span>
            <span>Deliveries</span>
          </a>
          <div className="rail-foot">
            <a href="#" onClick={(e) => (e.preventDefault(), toggleRail())} title="Collapse rail (Ctrl+B)">
              <span className="ico">{railCollapsed ? "»" : "«"}</span>
              <span>Collapse</span>
            </a>
          </div>
        </nav>
        <div className={`shell-main${dockOpen ? " dock-open" : ""}`} style={{ "--dock-h": `${dockHeight}px` } as CSSProperties}>
          <main className="workspace">
            <Outlet />
          </main>
          {dockOpen ? (
            <Dock tab={dockTab} setTab={(t) => setDock(true, t)} close={() => setDock(false)} height={dockHeight} setHeight={setDockHeight} />
          ) : null}
        </div>
        {assistantOpen ? <AssistantPanel /> : null}
      </div>
      {!dockOpen ? (
        <button className="btn sm dock-toggle" onClick={() => setDock(true)} title="Show dock (Ctrl+J)">
          ▲ dock
        </button>
      ) : null}
      {creating ? <CreateProjectDialog onClose={() => setCreating(false)} /> : null}
      <Toasts />
    </div>
  );
}

function RunIndicator({ onClick }: { onClick: () => void }) {
  const projectId = useSession((s) => s.projectId);
  const runs = useRuns(projectId);
  const active = runs.data?.find((r) => r.status === "running" || r.status === "queued");
  const last = runs.data?.[0];
  const r = active ?? last;
  if (!r) return null;
  return (
    <button className="btn ghost sm" onClick={onClick} title={`run ${r.id}`}>
      <StatusDot status={r.status} />
      {active ? `running ${r.progress.done}/${r.progress.total}` : `last run: ${r.summary?.fires ?? 0} fires`}
    </button>
  );
}

function Dock({
  tab,
  setTab,
  close,
  height,
  setHeight,
}: {
  tab: DockTab;
  setTab: (t: DockTab) => void;
  close: () => void;
  height: number;
  setHeight: (h: number) => void;
}) {
  const byFile = useProblems((s) => s.byFile);
  const jump = useProblems((s) => s.jump);
  const problems = useMemo(() => allProblems(byFile), [byFile]);
  const currentRunId = useSession((s) => s.currentRunId);
  const run = useRun(currentRunId);
  const navigate = useNavigate();
  const errors = problems.filter((p) => p.severity === "error").length;
  const events = run.data?.manifest?.flights.flatMap((f) => f.events.map((e) => ({ ...e, flight: f.flight_id }))) ?? [];
  return (
    <div className="dock">
      <EdgeResizer
        axis="y"
        invert
        className="dock-resizer"
        value={height}
        onChange={(h) => setHeight(Math.min(h, window.innerHeight * 0.75))}
        onReset={() => setHeight(220)}
        title="Drag to resize dock · double-click to reset · Ctrl+J to hide"
      />
      <div className="dock-tabs">
        <button className={tab === "problems" ? "on" : ""} onClick={() => setTab("problems")}>
          Problems
          {problems.length ? <span className={`badge ${errors ? "err" : "warn"}`}>{problems.length}</span> : null}
        </button>
        <button className={tab === "runlog" ? "on" : ""} onClick={() => setTab("runlog")}>
          Run log{run.data ? <span className="badge">{run.data.status}</span> : null}
        </button>
        <button className={tab === "events" ? "on" : ""} onClick={() => setTab("events")}>
          Events{events.length ? <span className="badge">{events.length}</span> : null}
        </button>
        <button className={tab === "deliveries" ? "on" : ""} onClick={() => setTab("deliveries")}>
          Deliveries
        </button>
        <span className="spacer" />
        <button className="btn ghost sm" onClick={close} title="Hide dock (Ctrl+J)">
          ▼
        </button>
      </div>
      <div className="dock-body">
        {tab === "problems" ? (
          problems.length ? (
            problems.map((p, i) => (
              <div
                key={i}
                className="problem"
                onClick={() => {
                  navigate("/triggers");
                  jump?.(p.file, p.line, p.col);
                }}
              >
                <span className={`sev-${p.severity}`}>{p.severity}</span>
                <span className="dim loc" title={`${p.file}:${p.line}:${p.col}`}>
                  {p.file.split("/").pop()}:{p.line}:{p.col}
                </span>
                <span>
                  {p.message} <span className="faint">({p.code})</span>
                </span>
              </div>
            ))
          ) : (
            <span className="dim">No problems.</span>
          )
        ) : null}
        {tab === "runlog" ? (
          run.data ? (
            <>
              <div className="dim">
                run {run.data.id} · {run.data.status}
                {run.data.error ? <span className="badge err">{run.data.error}</span> : null}
              </div>
              {run.data.log.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </>
          ) : (
            <span className="dim">No run selected. Start one from Flights or Triggers.</span>
          )
        ) : null}
        {tab === "events" ? (
          events.length ? (
            events.map((e, i) => (
              <div key={i}>
                <span className="dim">{e.flight}</span> t={e.t} <span className={e.kind === "error" ? "sev-error" : ""}>{e.kind}</span>{" "}
                {e.trigger}: {e.message}
              </div>
            ))
          ) : (
            <span className="dim">No events.</span>
          )
        ) : null}
        {tab === "deliveries" ? <DeliveriesPanel /> : null}
      </div>
    </div>
  );
}
